/**
 * VÉRAULT Portal — Needed Confirmations (Feb 2026 · QA correction pass)
 *
 * Renders the question library returned by the API, persists answers via
 * the client-portal write endpoint (or advisor PATCH when used in the
 * advisor preview), and surfaces the live completion / submission state.
 *
 * Modes
 *  - `filterToSection={key}` → renders only questions linked to a section
 *    and respecting placement (after / before / inside; never summary_only)
 *  - default (no filter) → bottom Needed Confirmations Summary. Shows ONLY
 *    incomplete items as compact tracker rows + summary-only questions in
 *    full (their canonical placement), plus the Submit button.
 */
import React from "react";
import { CheckCircle2, Circle, Loader2, Lock, Send, ArrowDown } from "lucide-react";
import { Surface, EditorialRule, api } from "../index";
import { toast } from "sonner";

function isAdvisorPreview() {
  if (typeof window === "undefined") return false;
  return (window.location.pathname || "").includes("/portal/advisor/");
}

function isAnswered(q, answer) {
  if (!answer) return false;
  if (typeof answer === "string") return answer.length > 0;
  if (typeof answer !== "object") return false;
  if (typeof answer.choice === "string" && answer.choice) return true;
  if (Array.isArray(answer.choices) && answer.choices.length > 0) return true;
  // field-type questions or follow-up fields populated
  const meaningful = Object.entries(answer).some(
    ([k, v]) => k !== "choice" && k !== "choices" && v !== null && v !== "" && v !== "no" && !(Array.isArray(v) && v.length === 0)
  );
  return meaningful;
}

function readStatusLabel(status, submitted) {
  if (submitted) return "Confirmations submitted";
  if (status === "ready-for-advisor-review") return "Confirmations complete";
  return "A few details remaining";
}

function scrollToQuestion(key) {
  if (typeof document === "undefined") return;
  const el = document.querySelector(`[data-testid="smart-confirmation-card-${key}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-[hsl(var(--accent-gold))]", "ring-offset-4");
  setTimeout(() => el.classList.remove("ring-2", "ring-[hsl(var(--accent-gold))]", "ring-offset-4"), 2000);
}

export default function SmartConfirmations({
  tripId,
  readOnly = false,
  filterToSection = null,
  filterToAnchor = null,     // when set, only show questions with anchor_item_id === filterToAnchor
  excludeAnchored = false,   // when true, skip any question that has an anchor_item_id (they render next to their item card)
  hideHeader = false,
  hideEmpty = false,
}) {
  const [bundle, setBundle] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [lastSaved, setLastSaved] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const isPreview = isAdvisorPreview();

  const fetchAnswers = React.useCallback(async (silent = false) => {
    if (!tripId) return;
    if (!silent) setLoading(true);
    try {
      const path = isPreview
        ? `/design-studio/${tripId}/smart-answers`
        : `/client-portal/trips/${tripId}/smart-answers`;
      const { data } = await api.get(path);
      setBundle(data);
    } catch {
      setBundle(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tripId, isPreview]);

  React.useEffect(() => { fetchAnswers(); }, [fetchAnswers]);

  React.useEffect(() => {
    const handler = () => fetchAnswers(true);
    const savedHandler = () => setLastSaved(new Date());
    window.addEventListener("vt:smart-answers:refresh", handler);
    window.addEventListener("vt:smart-answers:saved", savedHandler);
    return () => {
      window.removeEventListener("vt:smart-answers:refresh", handler);
      window.removeEventListener("vt:smart-answers:saved", savedHandler);
    };
  }, [fetchAnswers]);

  const persist = React.useCallback(
    async (patch) => {
      if (!tripId || readOnly) return;
      setSaving(true);
      try {
        const path = isPreview
          ? `/design-studio/${tripId}/smart-answers`
          : `/client-portal/trips/${tripId}/smart-answers`;
        const body = { answers: patch };
        const res = isPreview ? await api.patch(path, body) : await api.post(path, body);
        setBundle(res.data);
        setLastSaved(new Date());
        window.dispatchEvent(new CustomEvent("vt:smart-answers:refresh"));
        window.dispatchEvent(new CustomEvent("vt:smart-answers:saved"));
      } catch {
        toast.error("We couldn't save that answer. Please try again.");
      } finally {
        setSaving(false);
      }
    },
    [tripId, isPreview, readOnly]
  );

  const submit = React.useCallback(async () => {
    if (!tripId || readOnly || isPreview) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/client-portal/trips/${tripId}/smart-answers/submit`, {}
      );
      setBundle(data);
      window.dispatchEvent(new CustomEvent("vt:smart-answers:refresh"));
      toast.success("Sent to your advisor.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit.");
    } finally {
      setSubmitting(false);
    }
  }, [tripId, readOnly, isPreview]);

  // Loading / empty states — invisible when woven inline with `hideEmpty`
  if (loading) {
    if (hideEmpty) return null;
    return (
      <section className="space-y-6 pt-6" data-testid="client-smart-confirmations">
        {!hideHeader && <EditorialRule label="Needed Confirmations" />}
        <Surface variant="paper" padded className="!p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Loading…</p>
        </Surface>
      </section>
    );
  }
  if (!bundle) {
    if (hideEmpty) return null;
    return (
      <section className="space-y-6 pt-6" data-testid="client-smart-confirmations">
        {!hideHeader && <EditorialRule label="Needed Confirmations" />}
        <Surface variant="paper" padded className="!p-5">
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            Your confirmation cards will open here once your advisor publishes the proposal.
          </p>
        </Surface>
      </section>
    );
  }

  const { template, answers, status, answered_required, required_total, submitted_at } = bundle;
  const allQuestions = (template?.questions || []).filter((q) => (q.placement || "after") !== "hidden");
  const requiredKeys = new Set(template?.required_keys || []);

  // ─── INLINE mode: questions woven into a section ────────────────
  if (filterToSection) {
    const inline = allQuestions.filter((q) => {
      if (q.proposal_section !== filterToSection) return false;
      if ((q.placement || "after") === "summary_only") return false;
      const anchor = q.anchor_item_id || null;
      if (filterToAnchor) {
        // Only show questions anchored to THIS item
        return anchor === filterToAnchor;
      }
      if (excludeAnchored && anchor) {
        // Hide anchored questions — they render next to their item
        return false;
      }
      return true;
    });
    if (inline.length === 0) return null;
    return (
      <section
        className="space-y-4 my-6 pt-2"
        data-testid={
          filterToAnchor
            ? `client-smart-confirmations-anchor-${filterToAnchor}`
            : `client-smart-confirmations-section-${filterToSection}`
        }
        data-trip-id={tripId}
      >
        {inline.map((q) => (
          <ConfirmationCard
            key={q.key}
            question={q}
            answer={answers?.[q.key]}
            required={requiredKeys.has(q.key)}
            readOnly={readOnly}
            onPatch={(value) => persist({ [q.key]: value })}
          />
        ))}
      </section>
    );
  }

  // ─── BOTTOM SUMMARY mode (no filter) ────────────────────────────
  // Show:
  //   1) Any question with placement === "summary_only" → full card
  //   2) Incomplete items elsewhere → compact tracker row (link back)
  //   3) Submit Confirmations button (client only)
  const summaryOnly = allQuestions.filter((q) => (q.placement || "after") === "summary_only");
  const inlineIncomplete = allQuestions.filter((q) => {
    const placement = q.placement || "after";
    if (placement === "summary_only" || placement === "hidden") return false;
    return !isAnswered(q, answers?.[q.key]);
  });
  const submitted = Boolean(submitted_at);
  const allRequiredAnswered = required_total > 0 && answered_required >= required_total;
  const showSubmitButton = !readOnly && !isPreview;

  return (
    <section
      className="space-y-6 pt-8 mt-8 border-t border-[rgba(0,0,0,0.06)]"
      data-testid="client-smart-confirmations"
      data-trip-id={tripId}
    >
      {!hideHeader && (
        <div className="text-center space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
            A Moment to Reflect
          </p>
          <h2 className="font-serif text-[26px] sm:text-[30px] leading-tight text-[hsl(var(--foreground))]">
            Needed Confirmations
          </h2>
          <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))] max-w-xl mx-auto leading-relaxed">
            Once you have reviewed the proposal and answered the remaining details, I will refine the
            design, confirm pricing, and return with the final itinerary direction for your approval.
          </p>
        </div>
      )}

      {/* Summary-only full cards (rare — usually empty) */}
      {summaryOnly.length > 0 && (
        <div className="space-y-4">
          {summaryOnly.map((q) => (
            <ConfirmationCard
              key={q.key}
              question={q}
              answer={answers?.[q.key]}
              required={requiredKeys.has(q.key)}
              readOnly={readOnly}
              onPatch={(value) => persist({ [q.key]: value })}
            />
          ))}
        </div>
      )}

      {/* Tracker — counts + compact incomplete rows */}
      <Surface variant="paper" padded className="!p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]"
               data-testid="smart-confirmations-status">
              {readStatusLabel(status, submitted)}
            </p>
            <p className="mt-2 font-serif text-[16px] leading-relaxed text-[hsl(var(--foreground))]"
               data-testid="smart-confirmations-counts">
              <span className="text-[hsl(var(--foreground))] font-medium">{required_total}</span>
              {" required · "}
              <span className="text-[hsl(var(--foreground))] font-medium">{answered_required}</span>
              {" answered · "}
              <span className="text-[hsl(var(--foreground))] font-medium">{Math.max(0, required_total - answered_required)}</span>
              {" remaining"}
            </p>
          </div>
          <div className="shrink-0">
            {saving && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--background-paper))]/60 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]"
                    data-testid="smart-confirmations-saving">
                <Loader2 className="size-3 animate-spin" strokeWidth={1.5} />
                Autosaving…
              </span>
            )}
            {!saving && lastSaved && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--accent-gold))]/10 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]"
                    data-testid="smart-confirmations-saved">
                <CheckCircle2 className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                Progress saved
              </span>
            )}
            {submitted && !saving && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--accent-gold))]/15 font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]"
                    data-testid="smart-confirmations-submitted-pill">
                <CheckCircle2 className="size-3" strokeWidth={1.5} />
                Submitted {new Date(submitted_at).toLocaleDateString("en-US")}
              </span>
            )}
          </div>
        </div>

        {/* Compact incomplete tracker rows */}
        {inlineIncomplete.length > 0 ? (
          <ul className="space-y-1.5 pt-2 border-t border-[rgba(0,0,0,0.05)]">
            {inlineIncomplete.map((q) => (
              <li key={q.key}
                  className="flex items-center justify-between gap-3 py-1.5"
                  data-testid={`smart-confirmation-tracker-${q.key}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Circle className="size-3 shrink-0 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
                  <span className="font-serif text-[14.5px] text-[hsl(var(--foreground))] truncate">{q.label}</span>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] shrink-0">
                    {requiredKeys.has(q.key) ? "required" : "optional"} · incomplete
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => scrollToQuestion(q.key)}
                  className="shrink-0 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:text-[hsl(var(--foreground))]"
                  data-testid={`smart-confirmation-tracker-${q.key}-jump`}>
                  <ArrowDown className="size-3" strokeWidth={1.5} />
                  Jump to question
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="pt-3 border-t border-[rgba(0,0,0,0.05)] font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">
            All required details are answered. {readOnly ? "" : "Press Submit when you are ready."}
          </p>
        )}

        {readOnly && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(var(--background-paper))]/60 border border-[rgba(0,0,0,0.05)]">
            <Lock className="size-3 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">Read-only view.</p>
          </div>
        )}
      </Surface>

      {/* Submit button + reassurance */}
      {showSubmitButton && (
        <div className="flex flex-col items-center gap-3 text-center pt-2">
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--accent-gold))]/25 hover:bg-[hsl(var(--accent-gold))]/45 disabled:opacity-50 font-mono text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--foreground))] border border-[hsl(var(--accent-gold))]/40"
            data-testid="smart-confirmations-submit"
          >
            {submitting ? (<Loader2 className="size-4 animate-spin" strokeWidth={1.5} />) : (<Send className="size-4" strokeWidth={1.5} />)}
            {submitted ? "Send updated responses" : "Submit Confirmations"}
          </button>
          <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] max-w-md leading-relaxed">
            {submitted
              ? `Submitted ${new Date(submitted_at).toLocaleString()}. Your answers autosave as you go, and you may revise them before final submission.`
              : allRequiredAnswered
                ? "Your answers autosave as you go, and you may revise them before final submission."
                : "Your answers autosave as you go. You may submit when ready — your advisor will see what is still open."}
          </p>
        </div>
      )}
    </section>
  );
}

function ConfirmationCard({ question, answer, required, readOnly, onPatch }) {
  const isMulti = question.type === "multi-choice";
  const choice = (answer && typeof answer === "object" && answer.choice) || null;
  const choices = (answer && typeof answer === "object" && Array.isArray(answer.choices)) ? answer.choices : [];
  const followUps =
    (choice && question.follow_up_fields && question.follow_up_fields[choice]) || [];
  const answered = isAnswered(question, answer);

  function setChoice(opt) {
    if (readOnly) return;
    if (isMulti) {
      const next = new Set(choices);
      if (next.has(opt.id)) next.delete(opt.id);
      else next.add(opt.id);
      onPatch({ ...(answer || {}), choices: Array.from(next) });
    } else {
      onPatch({ ...(answer || {}), choice: opt.id });
    }
  }
  function setField(fieldId, value) {
    if (readOnly) return;
    onPatch({ ...(answer || {}), [fieldId]: value });
  }
  function clearChoice() {
    if (readOnly) return;
    onPatch(null);
  }

  return (
    <Surface
      variant="paper"
      padded
      className="!p-6 space-y-4 transition-shadow scroll-mt-24"
      data-testid={`smart-confirmation-card-${question.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {answered ? (
              <CheckCircle2 className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            ) : (
              <Circle className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            )}
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--foreground-muted))]">
              Needed Confirmation
              {required && <span className="ml-2 text-[hsl(var(--accent-gold))]">· Required</span>}
            </p>
          </div>
          <h3 className="mt-2 font-serif text-[18px] sm:text-[20px] leading-tight text-[hsl(var(--foreground))]">
            {question.label}
          </h3>
          {question.intro && (
            <p className="mt-2 font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">
              {question.intro}
            </p>
          )}
          <p className="mt-2 font-serif text-[15px] sm:text-[16px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
            {question.prompt}
          </p>
        </div>
        {(choice || choices.length > 0) && !readOnly && (
          <button
            type="button"
            onClick={clearChoice}
            className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors"
            data-testid={`smart-confirmation-${question.key}-clear`}
          >
            Clear
          </button>
        )}
      </div>

      {Array.isArray(question.options) && question.options.length > 0 && (
        <ul className="space-y-2 mt-2">
          {question.options.map((opt) => {
            const selected = isMulti ? choices.includes(opt.id) : choice === opt.id;
            return (
              <li key={opt.id}>
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => setChoice(opt)}
                  className={
                    "w-full text-left flex items-start gap-2.5 px-4 py-3 rounded-md border transition-colors " +
                    (selected
                      ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10"
                      : "border-[rgba(0,0,0,0.08)] bg-white/40 hover:bg-white/70")
                  }
                  data-testid={`smart-confirmation-${question.key}-option-${opt.id}`}
                >
                  {selected ? (
                    <CheckCircle2 className="size-3.5 mt-0.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                  ) : (
                    <Circle className="size-3.5 mt-0.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
                  )}
                  <p className="font-serif text-[14.5px] leading-relaxed text-[hsl(var(--foreground))]">
                    {opt.label}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {Array.isArray(question.fields) && question.fields.length > 0 && (
        <div className="space-y-2 mt-2">
          {question.fields.map((f) => (
            <FieldRow
              key={f.id}
              field={f}
              value={(answer && answer[f.id]) || ""}
              onChange={(v) => setField(f.id, v)}
              readOnly={readOnly}
              testIdBase={`smart-confirmation-${question.key}-field-${f.id}`}
            />
          ))}
        </div>
      )}

      {Array.isArray(followUps) && followUps.length > 0 && (
        <div className="space-y-2 mt-2 pl-3 border-l-2 border-[hsl(var(--accent-gold))]/40">
          {followUps.map((f) => (
            <FieldRow
              key={f.id}
              field={f}
              value={(answer && answer[f.id]) || ""}
              onChange={(v) => setField(f.id, v)}
              readOnly={readOnly}
              testIdBase={`smart-confirmation-${question.key}-followup-${f.id}`}
            />
          ))}
        </div>
      )}

      {question.helper && (
        <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">
          {question.helper}
        </p>
      )}
    </Surface>
  );
}

function FieldRow({ field, value, onChange, readOnly, testIdBase }) {
  const baseInputCls =
    "flex-1 bg-transparent border-b border-[rgba(0,0,0,0.1)] focus:border-[hsl(var(--accent-gold))] outline-none font-accent italic text-[12.5px] text-[hsl(var(--foreground))] px-1 py-0.5";
  if (field.type === "single-choice") {
    return (
      <div className="px-3 py-2 rounded-md border border-dashed border-[rgba(0,0,0,0.1)] space-y-1.5">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
          {field.label}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(field.options || []).map((opt) => (
            <button key={opt.id} type="button" disabled={readOnly}
              onClick={() => onChange(opt.id)}
              data-testid={`${testIdBase}-${opt.id}`}
              className={
                "px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.14em] border transition-colors " +
                (value === opt.id
                  ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--foreground))]"
                  : "border-[rgba(0,0,0,0.1)] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]")
              }>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "yesno" || field.type === "checkbox") {
    const yesActive = value === "yes" || value === true;
    return (
      <div className="flex items-baseline justify-between gap-3 px-3 py-2 rounded-md border border-dashed border-[rgba(0,0,0,0.1)]"
           data-testid={testIdBase}>
        <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">{field.label}</p>
        <div className="flex gap-1.5">
          {["yes", "no"].map((v) => (
            <button key={v} type="button" disabled={readOnly}
              onClick={() => onChange(v)}
              data-testid={`${testIdBase}-${v}`}
              className={
                "px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.14em] border transition-colors " +
                ((v === "yes" && yesActive) || (v === "no" && value === "no")
                  ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--foreground))]"
                  : "border-[rgba(0,0,0,0.1)] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]")
              }>
              {v}
            </button>
          ))}
        </div>
      </div>
    );
  }
  const inputType = field.type === "date" ? "date" : field.type === "number" ? "number" : "text";
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2 rounded-md border border-dashed border-[rgba(0,0,0,0.1)]">
      <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] shrink-0">{field.label}</p>
      <input
        type={inputType}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className={baseInputCls + " text-right"}
        data-testid={testIdBase}
        placeholder={field.placeholder || "—"}
      />
    </div>
  );
}
